import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCountriesQuery } from '../../../../../../../../../graphql/fragment/audiences/ListCountries.generated.tsx';
import type { ListRegionsQuery } from '../../../../../../../../../graphql/fragment/audiences/ListRegions.generated.tsx';
import type { FormSegment } from '../../../../../../../../../modules/audiences/AudienceForm/types.ts';
import { SegmentCategory } from '../../../../hooks/types.ts';
import { useReferenceData } from '../../../../hooks/useReferenceData.ts';
import { SegmentAlert } from '../../../expandableSections/segments/SegmentAlert.tsx';
import { createBasicInfoPropsFromSegment } from '../../../utils/createBasicInfoProps.ts';
import { getSegmentExpirationAlertProps } from '../../../utils/getSegmentExpirationAlertProps.ts';
import { BasicInfo } from '../SegmentListItem/BasicInfo.tsx';
import { MarketMetadata } from '../SegmentListItem/MarketMetadata.tsx';
import { SegmentHeader } from '../SegmentListItem/SegmentHeader.tsx';
import { itemStyles } from '../SegmentListItem/styles.ts';
import { useSegmentHeaderElements } from '../SegmentListItem/useSegmentHeaderElements.tsx';
import { createSegmentMetadata } from '../SegmentsList/utils/createSegmentMetadata.ts';
import { formatReachValue } from '../SegmentsList/utils/formatReachValue.ts';

export type SelectedSegmentItemProps = {
  segment: FormSegment;
  onRemove: () => void;
  dataTest?: string;
  regions: ListRegionsQuery['region'];
  countries: ListCountriesQuery['country'];
};

export const SelectedSegmentItem = ({
  segment,
  onRemove,
  dataTest = 'selected-segment-item',
  regions,
  countries,
}: SelectedSegmentItemProps) => {
  const { organizations, providers } = useReferenceData();
  const { t } = useTranslation();

  const metadata = useMemo(() => createSegmentMetadata(segment, t, segment.account_id || ''), [segment, t]);
  const displayReach = formatReachValue(t, metadata.reach);
  const organizationName = segment.account_id ? organizations[segment.account_id] : null;

  const displayedOrganizationName =
    organizationName === 'OpenAudience' || segment.category === 'predefined'
      ? 'OpenXSelect'
      : organizationName || t('N / A');
  const providerName = segment.provider_id ? providers[segment.provider_id] : null;
  const displayProviderName =
    providerName === 'OpenAudience' || segment.category === 'predefined' ? 'OpenXSelect' : providerName || t('N / A');

  const headerConfig = useSegmentHeaderElements({ mode: 'removable', onRemove });
  const hasMarketMetadata = metadata.regionCodes.length > 0 || metadata.countryCodes.length > 0;

  const basicInfoProps = useMemo(
    () =>
      createBasicInfoPropsFromSegment({
        segment,
        id: metadata.id,
        reach: metadata.reach,
        displayReach,
        category: metadata.category || '',
        organizationName: displayedOrganizationName,
        providerName: displayProviderName || null,
      }),
    [segment, metadata, displayReach, displayedOrganizationName, displayProviderName]
  );

  return (
    <ListItem data-test={dataTest} sx={itemStyles({ isSelected: false })}>
      <Stack
        className="segment-list-item"
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignContent: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ minWidth: 0, overflow: 'hidden', flexGrow: 1 }}>
          <SegmentHeader
            name={metadata.name}
            showPredefinedSegmentBadge={segment.category === SegmentCategory.PREDEFINED}
            isLAL={false}
            config={headerConfig}
            isSelectedItem
            expirationAlert={<SegmentAlert {...getSegmentExpirationAlertProps(segment)} />}
          />

          <BasicInfo {...basicInfoProps} />

          {hasMarketMetadata && (
            <MarketMetadata
              regionCodes={metadata.regionCodes}
              countryCodes={metadata.countryCodes}
              regions={regions}
              countries={countries}
              dataTest="segment-market-metadata"
            />
          )}
        </Box>

        {headerConfig.rightIcon}
      </Stack>
    </ListItem>
  );
};
